import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import { FinalizeChewyActiveRxTable } from '~/components/dashboard/soapV2/finalize-orders/chewy-active-rx/FinalizeChewyActiveRxTable'
import { useCanSubmitApprovedChewyActiveRx } from '~/hooks/useCanSubmitApprovedChewyActiveRx'
import {
  getChargeSheetPendingChewyActiveRxList,
  getIsFetchingAllPendingActiveRxForChargeSheet,
  getIsSubmittingAllActiveRx,
  getSubmittingAllActiveRxError,
  submitAllActiveRxForChargeSheet,
} from '~/store/duck/prescriptions'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useHasChewyActiveRxApproved } from '~/store/hooks/prescription'
import { removeServerErrorPrefix } from '~/utils/errors'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

const useStyles = makeStyles(
  (theme) => ({
    paper: {
      maxWidth: 800,
    },
    actions: {
      borderTop: 0,
      justifyContent: 'flex-start',
      paddingBottom: theme.spacing(2),
      gap: theme.spacing(2),
    },
    content: {
      minHeight: 116,
    },
    title: {
      borderBottom: 0,
    },
    table: {
      border: theme.constants.tableBorder,
    },
  }),
  { name: 'FinalizeChewyActiveRxDialog' },
)

interface FinalizeChewyActiveRxDialogProps extends BasePuiDialogProps {
  businessId: string
  clientId: string
}

export const FinalizeChewyActiveRxDialog = ({
  businessId,
  clientId,
  onClose,
  open,
}: FinalizeChewyActiveRxDialogProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common'])
  const dispatch = useDispatch()

  const error = useSelector(getSubmittingAllActiveRxError)
  const isSubmitting = useSelector(getIsSubmittingAllActiveRx)
  const isFetchingAllPendingActiveRxForChargeSheet = useSelector(
    getIsFetchingAllPendingActiveRxForChargeSheet,
  )
  const chargeSheetPendingChewyActiveRxList = useSelector(
    getChargeSheetPendingChewyActiveRxList,
  )

  const canSubmitApprovedChewyActiveRx = useCanSubmitApprovedChewyActiveRx()
  const hasChewyActiveRxApproved = useHasChewyActiveRxApproved(
    chargeSheetPendingChewyActiveRxList,
  )
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()

  const canFinalize = canSubmitApprovedChewyActiveRx && hasChewyActiveRxApproved

  const closeOnUpdate = useCloseAfterCreation(() => {
    if (onClose && !error) {
      onClose()
    }
  }, getIsSubmittingAllActiveRx)

  const sendToChewy = () => {
    dispatch(submitAllActiveRxForChargeSheet({ businessId, clientId }))
    closeOnUpdate()
  }

  return (
    <PuiDialog
      {...(isChewyCheckoutEnabled
        ? {}
        : {
            actions: (
              <>
                {canFinalize && (
                  <ButtonWithLoader
                    loading={isSubmitting}
                    variant="contained"
                    onClick={sendToChewy}
                  >
                    {t('Common:SEND_TO_CHEWY')}
                  </ButtonWithLoader>
                )}
                <Grid>{t('Soap:FINALIZE_CHEWY_ACTIVE_RX_DESCRIPTION')}</Grid>
              </>
            ),
          })}
      aria-labelledby="finalize-chewy-active-rx-dialog"
      classes={{
        dialogTitle: classes.title,
        paper: classes.paper,
        actions: classes.actions,
        dialogContentRoot: classes.content,
      }}
      open={open}
      title={
        isChewyCheckoutEnabled
          ? t('Common:FINALIZE_AND_APPROVE_CHEWY_RX')
          : t('Common:FINALIZE_AND_SUBMIT_CHEWY_RX')
      }
      onClose={onClose}
    >
      <Grid className={classes.table} m={2} mt={isChewyCheckoutEnabled ? 1 : 2}>
        <FinalizeChewyActiveRxTable
          context="Charge-sheet"
          data={chargeSheetPendingChewyActiveRxList}
          isLoading={isFetchingAllPendingActiveRxForChargeSheet}
        />
      </Grid>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {removeServerErrorPrefix(error)}
        </Alert>
      )}
    </PuiDialog>
  )
}
